import { Phone } from "@/interfaces/phone";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { ToastInterface, useToast } from "vue-toastification";

interface Props {
  immediate?: boolean;
  params?: Params;
}

interface Params {
  id: string | number;
}

interface CreateParams {
  id: string | number;
  countryCodeId: number;
  extension?: string;
  phone: string;
  type: string;
}

const getContactPhones = async (params: Params): Promise<Phone> => {
  const { data } = await axios.get<Phone>(
    `/api/internal/contacts/${params.id}/phones`,
  );
  return data;
};

const createContactPhone = async (
  params: CreateParams,
  toast: ToastInterface,
) => {
  try {
    const { data } = await axios.post(
      `/api/internal/contacts/${params.id}/phones`,
      params,
    );
    toast.success("Se creo correctamente el teléfono");

    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const updateContactPhone = async (
  params: CreateParams & { phoneId: string | number },
  toast: ToastInterface,
) => {
  try {
    const { data } = await axios.put(
      `/api/internal/contacts/${params.id}/phones/${params.phoneId}`,
      params,
    );
    toast.success("Se actualizo correctamente el teléfono");

    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const useContactPhones = (props: Props = {}) => {
  const toast = useToast();

  const { data, isLoading, isError, refetch, isFetching } = useQuery(
    ["contact-phones", props.params],
    () => getContactPhones(props.params!),
    { enabled: props.immediate },
  );

  const createContactPhoneMutation = useMutation((params: CreateParams) =>
    createContactPhone(params, toast),
  );

  const updateContactPhoneMutation = useMutation(
    (params: CreateParams & { phoneId: string | number }) =>
      updateContactPhone(params, toast),
  );

  return {
    contactPhones: data,
    isLoading,
    isError,
    refetch,
    isFetching,

    createContactPhone: createContactPhoneMutation.mutate,
    createContactPhoneAsync: createContactPhoneMutation.mutateAsync,
    createContactPhoneLoading: createContactPhoneMutation.isLoading,

    updateContactPhone: updateContactPhoneMutation.mutate,
    updateContactPhoneAsync: updateContactPhoneMutation.mutateAsync,
    updateContactPhoneLoading: updateContactPhoneMutation.isLoading,
  };
};

export default useContactPhones;
